import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { TAlbum, TTrack } from "@store/actions/track";

import { useModalAnimation } from "@/hooks/useModalAnimation";

import { BottomFade, ScrollChevron, ScrollHint, useScrollAffordance } from "../CartModal/phoneTemplate";
import DownloadPage, { EDownloadModalType } from "./components/DownloadPage";
import NextStepsPage from "./components/NextStepsPage";
import * as S from "./components/styles";

interface INewDownloadModal {
  open: boolean;
  onClose: () => void;
  activeStep: number;
  handleNext: () => void;
  onOpenLicense?: () => void;
  item?: TAlbum;
  song?: TTrack;
  composer: string;
  videoUrl?: string;
  spotifyUrl?: string;
  copyrightModal?: boolean;
  cover?: string;
  // Track is already licensed (opened from Orders): unlock WAV in step 1 and
  // show only the connect-your-video instruction in step 2 (no credit text).
  licensed?: boolean;
}

export enum EDownloadModal {
  FirstStep = 1,
  SecondStep,
}

const SLIDE_PADDING = 48; // 0px top + 48px bottom padding on SlidePanel

const NewDownloadModal: FC<INewDownloadModal> = ({
  open,
  onClose,
  item,
  song,
  handleNext,
  onOpenLicense,
  activeStep,
  composer,
  videoUrl = "",
  spotifyUrl = "",
  cover,
  licensed,
}) => {
  const { mounted, closing } = useModalAnimation(open);
  const [downloadStatus, setDownloadStatus] = useState<"idle" | "started" | "complete" | "error">("idle");
  const closingStepRef = useRef(activeStep - 1);
  if (!closing) closingStepRef.current = activeStep - 1;
  const step = closing ? closingStepRef.current : activeStep - 1;
  const viewportRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(viewportRef);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Animate viewport height between steps
  const prevStepRef = useRef(step);
  const applyHeight = useRef<() => void>();

  useEffect(() => {
    if (!open) return;
    const vp = viewportRef.current;
    if (!vp) return;

    const el = contentRefs.current[step];
    if (!el) return;

    const calc = () => {
      const maxH = window.innerHeight - 108;
      const contentH = el.offsetHeight + SLIDE_PADDING;
      const needsScroll = contentH > maxH;
      return { newH: needsScroll ? maxH : contentH, needsScroll };
    };

    const isStepChange = prevStepRef.current !== step;
    prevStepRef.current = step;
    const { newH, needsScroll } = calc();

    // Store resize handler for this step
    applyHeight.current = () => {
      const { newH: h, needsScroll: ns } = calc();
      vp.style.transition = "none";
      vp.style.height = `${h}px`;
      vp.style.overflowY = ns ? "auto" : "hidden";
    };

    if (!isStepChange) {
      vp.style.height = `${newH}px`;
      vp.style.overflowY = needsScroll ? "auto" : "hidden";
      return;
    }

    // Step changed: show scrollbar immediately if target step needs it
    vp.style.overflowY = needsScroll ? "auto" : "hidden";
    const currentH = vp.getBoundingClientRect().height;
    vp.style.transition = "none";
    vp.style.height = `${currentH}px`;
    void vp.offsetHeight;

    vp.style.transition = "height 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
    vp.style.height = `${newH}px`;
  }, [open, step, mounted]);

  // Re-evaluate height on window resize
  useEffect(() => {
    if (!open) return;
    const onResize = () => applyHeight.current?.();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const setContentRef = (index: number) => (el: HTMLDivElement | null) => {
    contentRefs.current[index] = el;
  };

  const handleDownloadNext = useCallback((status: "started" | "complete" | "error") => {
    setDownloadStatus(status);
    if (status === "started") {
      handleNext(); // advance to step 2 immediately
    }
  }, [handleNext]);

  if (!mounted) return null;

  const isAlbum = !!item;
  const media = isAlbum ? item : song;

  const composerName = composer.includes(" - ") ? composer.split(" - ")[0] : composer;
  const songName = composer.includes(" - ") ? composer.split(" - ")[1] : media?.name || "";

  return createPortal(
    <S.Overlay>
      <S.ModalContainer $closing={closing} onClick={(e) => e.stopPropagation()}>
        <S.ModalHead>
          <S.CloseBtn onClick={onClose} aria-label="Close">
            &#x2715;
          </S.CloseBtn>
        </S.ModalHead>

        {/* Slider */}
        <S.SliderViewport ref={viewportRef}>
          <S.SliderTrack $step={step}>
            {/* Step 1: Download */}
            <S.SlidePanel>
              <div ref={setContentRef(0)}>
                <DownloadPage
                  handleNext={handleDownloadNext}
                  onOpenLicense={onOpenLicense}
                  item={item}
                  song={song}
                  type={isAlbum ? EDownloadModalType.Album : EDownloadModalType.Song}
                  name={composer}
                  cover={cover}
                  wavAllowed={licensed}
                />
              </div>
            </S.SlidePanel>

            {/* Step 2: Next Steps */}
            <S.SlidePanel>
              <div ref={setContentRef(1)}>
                <NextStepsPage
                  composer={composerName}
                  songName={songName}
                  videoUrl={videoUrl}
                  spotifyUrl={spotifyUrl}
                  downloadStatus={downloadStatus}
                  licensed={licensed}
                />
              </div>
            </S.SlidePanel>
          </S.SliderTrack>
        </S.SliderViewport>

        {/* Scroll affordance — shows only while the step's content overflows
            (iOS hides the scrollbar for touch overflow). */}
        <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </S.ModalContainer>
    </S.Overlay>,
    document.body,
  );
};

export default NewDownloadModal;
